import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  deleteChecklistItem,
  fetchChecklistItems,
  updateChecklistItemSortOrder,
} from "@/modules/checklist/api";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import type { ChecklistInfo, ChecklistItem, FieldMapping, AssignmentOption } from "./checklist-item-editor/types";
import { getChecklistLevel } from "./checklist-item-editor/types";
import { ItemFormDialog } from "./checklist-item-editor/ItemFormDialog";
import { ItemRow } from "./checklist-item-editor/ItemRow";

interface ChecklistItemEditorProps {
  checklist: ChecklistInfo;
  onBack: () => void;
  embedded?: boolean;
  onRegisterAddItem?: (addItem: () => void) => void;
}

export function ChecklistItemEditor({ checklist, onBack, embedded = false, onRegisterAddItem }: ChecklistItemEditorProps) {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  const checklistLevel = getChecklistLevel(checklist);

  // Register the add item callback for parent components
  useEffect(() => {
    if (onRegisterAddItem) {
      onRegisterAddItem(() => setIsDialogOpen(true));
    }
  }, [onRegisterAddItem]);

  // --- Data queries ---

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["checklist-items", checklist.id],
    queryFn: async () => {
      const data = await fetchChecklistItems(checklist.tenant_id, checklist.id);
      return data.map((item) => ({
        ...item,
        is_inspection: item.is_inspection ?? false,
        dropdown_options: Array.isArray(item.dropdown_options)
          ? (item.dropdown_options as string[])
          : null,
        label_translations: item.label_translations as Record<string, string> | null,
        description_translations: item.description_translations as Record<string, string> | null,
        field_mappings: (item.field_mappings ?? []).map(m => ({
          field_mapping_id: m.field_mapping_id,
          display_order: m.display_order,
        })),
      })) as ChecklistItem[];
    },
  });

  const { data: areas = [] } = useQuery({
    queryKey: ["factory-areas", checklist.factory_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("factory_areas")
        .select("id, name, color")
        .eq("factory_id", checklist.factory_id)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    enabled: checklistLevel === "factory",
  });

  const { data: lines = [] } = useQuery({
    queryKey: ["production-lines-for-checklist", checklist.factory_id, checklist.area_id],
    queryFn: async () => {
      let query = supabase
        .schema('core')
        .from("production_lines")
        .select("id, name, area_id")
        .eq("factory_id", checklist.factory_id)
        .eq("is_active", true)
        .order("sort_order");
      if (checklist.area_id) {
        query = query.eq("area_id", checklist.area_id);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: checklistLevel === "factory" || checklistLevel === "area",
  });

  const { data: stations = [] } = useQuery({
    queryKey: ["stations-for-checklist", checklist.factory_id, checklist.line_id],
    queryFn: async () => {
      let query = supabase
        .schema('core')
        .from("stations")
        .select("id, name, line_id")
        .eq("factory_id", checklist.factory_id)
        .eq("is_active", true)
        .order("sort_order");
      if (checklist.line_id) {
        query = query.eq("line_id", checklist.line_id);
      } else if (checklist.area_id) {
        const lineIds = lines.map(l => l.id);
        if (lineIds.length > 0) {
          query = query.in("line_id", lineIds);
        }
      }
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: checklistLevel !== "station",
  });

  const { data: fieldMappings = [] } = useQuery({
    queryKey: ["field-mappings-for-checklist", checklist.factory_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("field_mappings")
        .select(`
          id,
          is_active,
          datapoint:datapoints(
            id,
            display_name,
            field_name,
            measurement_name,
            bucket_name,
            unit:units(symbol)
          )
        `)
        .eq("factory_id", checklist.factory_id)
        .eq("is_active", true);
      if (error) throw error;
      return data as FieldMapping[];
    },
  });

  // --- Assignment helpers ---

  const getAssignmentLabel = (item: ChecklistItem): string | null => {
    if (item.station_id) return stations.find(s => s.id === item.station_id)?.name || "Station";
    if (item.line_id) return lines.find(l => l.id === item.line_id)?.name || "Line";
    if (item.area_id) return areas.find(a => a.id === item.area_id)?.name || "Area";
    return null;
  };

  const buildAssignmentOptions = (): AssignmentOption[] => {
    const options: AssignmentOption[] = [{ value: "none", label: "No specific assignment" }];

    if (checklistLevel === "factory") {
      areas.forEach(area => {
        options.push({ value: `area:${area.id}`, label: area.name, indent: 0 });
        const areaLines = lines.filter(l => l.area_id === area.id);
        areaLines.forEach(line => {
          options.push({ value: `line:${line.id}`, label: line.name, indent: 1 });
          const lineStations = stations.filter(s => s.line_id === line.id);
          lineStations.forEach(station => {
            options.push({ value: `station:${station.id}`, label: station.name, indent: 2 });
          });
        });
      });
    } else if (checklistLevel === "area") {
      lines.forEach(line => {
        options.push({ value: `line:${line.id}`, label: line.name, indent: 0 });
        const lineStations = stations.filter(s => s.line_id === line.id);
        lineStations.forEach(station => {
          options.push({ value: `station:${station.id}`, label: station.name, indent: 1 });
        });
      });
    } else if (checklistLevel === "line") {
      stations.forEach(station => {
        options.push({ value: `station:${station.id}`, label: station.name, indent: 0 });
      });
    }

    return options;
  };

  const assignmentOptions = buildAssignmentOptions();

  // --- Mutations ---

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteChecklistItem(checklist.tenant_id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-items", checklist.id] });
      toast.success("Item deleted");
    },
    onError: (error) => toast.error("Failed to delete item: " + error.message),
  });

  const reorderMutation = useMutation({
    mutationFn: async ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }) => {
      if (fromIndex === toIndex) return;
      const reorderedItems = [...items];
      const [movedItem] = reorderedItems.splice(fromIndex, 1);
      reorderedItems.splice(toIndex, 0, movedItem);
      const updates = reorderedItems.map((item, idx) => ({ id: item.id, sort_order: idx }));
      for (const update of updates) {
        await updateChecklistItemSortOrder(checklist.tenant_id, update.id, update.sort_order);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-items", checklist.id] });
    },
    onError: (error) => toast.error("Failed to reorder: " + error.message),
  });

  // --- Drag-and-drop ---

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
  };

  const handleDragOver = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!draggedItemId) return;
    const fromIndex = items.findIndex(i => i.id === draggedItemId);
    if (fromIndex === targetIndex || fromIndex === targetIndex - 1) {
      setDropTargetIndex(null);
    } else {
      setDropTargetIndex(targetIndex);
    }
  };

  const handleDragLeave = () => setDropTargetIndex(null);

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (!draggedItemId) return;
    const fromIndex = items.findIndex(i => i.id === draggedItemId);
    if (fromIndex !== -1 && fromIndex !== targetIndex) {
      const adjustedTarget = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
      if (fromIndex !== adjustedTarget) {
        reorderMutation.mutate({ fromIndex, toIndex: adjustedTarget });
      }
    }
    setDraggedItemId(null);
    setDropTargetIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDropTargetIndex(null);
  };

  // --- Dialog management ---

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) setEditingItem(null);
  };

  const openEditDialog = (item: ChecklistItem) => {
    setEditingItem(item);
    setIsDialogOpen(true);
  };

  const nextSortOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;

  // --- Render ---

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">{checklist.name}</h2>
            <p className="text-sm text-muted-foreground">{checklist.description || "Edit checklist items"}</p>
          </div>
          <Button className="gap-2" onClick={() => setIsDialogOpen(true)}>
            <Plus className="w-4 h-4" />
            Add Item
          </Button>
        </div>
      )}

      <ItemFormDialog
        open={isDialogOpen}
        onOpenChange={handleDialogOpenChange}
        checklist={checklist}
        editingItem={editingItem}
        fieldMappings={fieldMappings}
        assignmentOptions={assignmentOptions}
        showAssignmentSelector={assignmentOptions.length > 1}
        nextSortOrder={nextSortOrder}
      />

      {isLoading ? (
        <div className="text-muted-foreground">Loading items...</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="font-medium mb-1">No items yet</h3>
            <p className="text-sm text-muted-foreground">
              Add check items that operators will complete during startup
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-0">
          {items.map((item, index) => (
            <ItemRow
              key={item.id}
              item={item}
              index={index}
              isDragging={draggedItemId === item.id}
              isDropTarget={dropTargetIndex === index}
              assignmentLabel={getAssignmentLabel(item)}
              onEdit={() => openEditDialog(item)}
              onDelete={() => deleteMutation.mutate(item.id)}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            />
          ))}
          {/* Drop zone at the end */}
          <div
            className={`transition-all duration-200 ease-out ${dropTargetIndex === items.length ? 'h-16 bg-primary/10 border-2 border-dashed border-primary rounded-lg' : 'h-4'}`}
            onDragOver={(e) => handleDragOver(e, items.length)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, items.length)}
          />
        </div>
      )}
    </div>
  );
}
